import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Card,
    CardBody,
    Chip,
    Button,
} from '@heroui/react';
import { Eye, BookOpen, Sparkles } from 'lucide-react';
import { useChat } from '@src/presentation/contexts/ChatContext';
import { ChatPascoMapper } from '@src/application/chat/mappers/chat-pasco.mapper';
import { useMobile } from '@src/shared/hooks/useMobile';
import { PascoDetailModal } from '@src/presentation/components/PascoDetailModal';

/**
 * ChatDocumentsViewer Component
 * Displays documents (pascos) associated with the currently selected chat message
 */
export default function ChatDocumentsViewer() {
    const navigate = useNavigate();
    const isMobile = useMobile();
    const { messages, selectedMessageId } = useChat();

    // Modal state for mobile
    const [selectedPascoId, setSelectedPascoId] = useState<string | null>(null);
    const [isModalOpen, setIsModalOpen] = useState(false);

    // Handle pasco click - open modal on mobile, navigate on desktop
    const handlePascoClick = (pascoId: string) => {
        if (isMobile) {
            setSelectedPascoId(pascoId);
            setIsModalOpen(true);
        } else {
            navigate(`/pascos/${pascoId}`);
        }
    };

    // Handle modal close
    const handleModalClose = () => {
        setIsModalOpen(false);
        setSelectedPascoId(null);
    };

    // Find the selected message
    const selectedMessage = useMemo(() => {
        return messages.find(msg => msg.id === selectedMessageId);
    }, [messages, selectedMessageId]);

    // Get the documents from the selected message
    const mostRelevantDocuments = selectedMessage?.mostRelevantPascos ? ChatPascoMapper.toDisplayPascos(selectedMessage.mostRelevantPascos) : [];
    const leastRelevantDocuments = selectedMessage?.leastRelevantPascos ? ChatPascoMapper.toDisplayPascos(selectedMessage.leastRelevantPascos) : [];

    // Fallback for backward compatibility: if no new properties, treat all pascos as most relevant
    if (mostRelevantDocuments.length === 0 && leastRelevantDocuments.length === 0 && selectedMessage?.allPascos) {
        mostRelevantDocuments.push(...ChatPascoMapper.toDisplayPascos(selectedMessage.allPascos));
    }

    if (!selectedMessageId || !selectedMessage || (mostRelevantDocuments.length === 0 && leastRelevantDocuments.length === 0)) {
        return null;
    }

    return (
        <>
            <div className="space-y-4">
                {/* Header */}
                <div className="flex items-center justify-between px-3 lg:px-0">
                    <div className="flex items-center gap-2">
                        <div className="p-1.5 rounded bg-primary/10">
                            <BookOpen className="h-4 w-4 text-primary" />
                        </div>
                        <div>
                            <div className='flex items-center gap-1'>
                                <h2 className="text-sm font-semibold text-foreground">
                                    Chat Documents
                                </h2>

                                <span className="inline-flex items-center gap-1 text-primary">
                                    <Sparkles className="h-4 w-4" />
                                    AI Results
                                </span>

                            </div>
                            <p className="text-xs text-foreground/60">
                                {(mostRelevantDocuments.length + leastRelevantDocuments.length)} document{(mostRelevantDocuments.length + leastRelevantDocuments.length) > 1 ? 's' : ''} from selected message
                            </p>
                        </div>
                    </div>
                    <Chip size="sm" variant="flat" color="primary">
                        {mostRelevantDocuments.length + leastRelevantDocuments.length}
                    </Chip>
                </div>

                {/* Documents Grid */}
                <div className="grid gap-3 grid-cols-1 sm:grid-cols-2 lg:grid-cols-2 xl:grid-cols-3 px-3 lg:px-0">
                    {/* Most Relevant */}
                    {mostRelevantDocuments.length > 0 && (
                        <>
                            <div className="col-span-full">
                                <h3 className="text-sm font-medium text-foreground mb-2">Most Relevant</h3>
                            </div>
                            {mostRelevantDocuments.map((pasco) => (
                                <DocumentCard key={pasco.pascoId} pasco={pasco} onPascoClick={handlePascoClick} />
                            ))}
                        </>
                    )}

                    {/* Least Relevant */}
                    {leastRelevantDocuments.length > 0 && (
                        <>
                            <div className="col-span-full">
                                <h3 className="text-sm font-medium text-foreground mb-2">
                                    {mostRelevantDocuments.length === 0 ? 'Most Relevant' : 'You might also be interested'}
                                </h3>
                            </div>
                            {leastRelevantDocuments.map((pasco) => (
                                <DocumentCard key={pasco.pascoId} pasco={pasco} onPascoClick={handlePascoClick} />
                            ))}
                        </>
                    )}
                </div>
            </div>

            {/* Pasco Detail Modal for Mobile */}
            <PascoDetailModal
                isOpen={isModalOpen}
                onClose={handleModalClose}
                pascoId={selectedPascoId}
            />
        </>
    );
}

interface DocumentCardProps {
    pasco: {
        pascoId: string;
        courseCode: string;
        courseName: string;
        academicYear: string;
        semester: number;
        department?: string;
        level: number;
        isInterimAssessment: boolean;
    };
    onPascoClick: (pascoId: string) => void;
}

function DocumentCard({ pasco, onPascoClick }: DocumentCardProps) {
    // No need to fetch, pasco data is already available

    return (
        <Card
            isPressable
            onPress={() => onPascoClick(pasco.pascoId)}
            className="border border-primary/30 bg-primary/5 rounded shadow-none hover:border-primary hover:shadow-custom transition-all active:scale-[0.98] h-full"
        >
            <CardBody className="p-3 h-full flex flex-col">
                <div className="flex flex-col gap-2 flex-1">
                    <div className="flex items-start justify-between gap-2">
                        <div className="flex-1 min-w-0">
                            <h3 className="font-semibold text-sm line-clamp-2 mb-1 text-foreground min-h-[2.5rem]">
                                {pasco.courseName}
                            </h3>
                            <p className="text-xs text-primary font-medium">
                                {pasco.courseCode}
                            </p>
                        </div>
                    </div>

                    <div className="flex flex-wrap gap-1">
                        <Chip size="sm" variant="flat" className="text-[10px]">
                            Level {pasco.level}
                        </Chip>
                        <Chip size="sm" variant="flat" color="primary" className="text-[10px]">
                            Semester {pasco.semester}
                        </Chip>
                        {pasco.isInterimAssessment && (
                            <Chip size="sm" variant="flat" color="secondary" className="text-[10px]">
                                Interim
                            </Chip>
                        )}
                    </div>

                    <div className="text-[11px] text-foreground/60 space-y-0.5 flex-1">
                        <p className="line-clamp-1">{pasco.department}</p>
                        <p>{pasco.academicYear}</p>
                    </div>
                </div>

                <Button
                    size="sm"
                    variant="flat"
                    color="primary"
                    startContent={<Eye className="h-3.5 w-3.5" />}
                    className="w-full rounded text-xs h-9 font-medium mt-2"
                    onPress={() => onPascoClick(pasco.pascoId)}
                >
                    View Details
                </Button>
            </CardBody>
        </Card>
    );
}
